/**
 * A portable exploration, carried in a URL fragment.
 *
 * The fragment matters: browsers do not send it in the HTTP request, so the
 * deploy serving Pathways never receives the graph. The document contains one
 * StoredSession and therefore no provider keys, preferences, corpus documents or
 * chat. On the way back in it goes through persist.ts's one untrusted-input codec;
 * there is deliberately no share-specific interpretation of verification data.
 *
 * What travels is the graph and its account, not the sender's retained raw
 * reasoning traces — see the strip in `explorationShareUrl`. The link is pasted
 * into chat threads, and the traces were both the bulk of the address and the
 * one private part of the document. Nor the two records that were most of what
 * was left: the exact model-call receipts, whose prompts restate the graph once
 * per pass, and the as-known replay's dated boards — see `linkPayload`. The copy
 * counts what its link left out, so it can say so where either would have shown.
 */

import { uid } from './id'
import { narrationWords } from './narrationReceipt'
import { readStoredSessionJson, storedSessionJson, type StoredSession } from './persist'
import type { Op, SharedCopy } from './types'

const PARAM = 'share'
const GZIP_FORMAT = 'g1'
const JSON_FORMAT = 'j1'
const PUBLIC_APP_HREF = 'https://explorepathways.pages.dev/'

/**
 * A practical ceiling below the roughly two-million-character address limit of
 * the narrowest browser this app targets. The storage codec's own caps keep an
 * honest document below this in normal use; this guard primarily stops a caller
 * from manufacturing an address the browser cannot navigate back to.
 */
const MAX_TOKEN_CHARS = 1_800_000
/** A decompression ceiling, before JSON.parse, so a tiny hostile fragment cannot
 * expand without bound. UTF-8 can take four bytes per stored character. */
const MAX_JSON_BYTES = 8_000_000

/**
 * Where an address stops being something a chat thread reliably carries. Not a
 * cap — the link still copies, `MAX_TOKEN_CHARS` is the only refusal — but the
 * point past which the UI owes the sender a warning instead of letting a
 * messaging app hang on a paste. The figure is judgement, not a spec: WhatsApp
 * truncates a message at 65,536 characters, Slack near 40,000, and iMessage
 * accepts any length but visibly chokes composing tens of thousands, so one
 * conservative number stands in for all of them.
 */
export const MESSAGING_STRAIN_CHARS = 20_000

export type SharedExplorationRead =
  | { kind: 'none' }
  | { kind: 'ok'; doc: StoredSession }
  | { kind: 'invalid' }
  | { kind: 'too-large' }
  | { kind: 'unsupported' }

export class ShareLinkError extends Error {
  constructor(readonly kind: 'too-large' | 'unavailable') {
    super(
      kind === 'too-large'
        ? 'This exploration is too large for a browser link.'
        : 'This browser cannot compress an exploration link.',
    )
    this.name = 'ShareLinkError'
  }
}

function isLoopback(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]'
  )
}

/**
 * Pick the app that will receive the fragment — the app, and nothing about the
 * page the link was copied from.
 *
 * A localhost address is meaningful only on the sender's machine, so a link
 * copied from the development app must not keep that origin. The public build is
 * the compatibility target by default; a self-hoster can name their deployed
 * copy explicitly. Otherwise the link goes to this deployment, at its mount: the
 * build's own base (`appBase`) resolved against the current address, so a subpath
 * deployment keeps `/pathways/` and nothing past it.
 *
 * And never the query (D332). A query is routing state for the page the link was
 * copied from — the hosted workspace's `?shared=<id>`, an invitation, an open
 * exploration — and a router reads it before anything reads the fragment. Kept,
 * it routed the recipient to the sender's page instead of the copy: a member of
 * the shared graph landed in the live graph, anyone else at a sign-in, and the
 * fragment was thrown away unread. A configured target loses its query too: it
 * names an app, and an app's address carries no routing state.
 */
function shareTarget(fromHref: string, configuredHref?: string, appBase?: string): URL {
  let current: URL
  try {
    current = new URL(fromHref)
  } catch {
    throw new ShareLinkError('unavailable')
  }

  const targetHref = configuredHref?.trim() || (isLoopback(current.hostname) ? PUBLIC_APP_HREF : '')
  let target: URL
  try {
    target = targetHref ? new URL(targetHref) : mountOf(current, appBase)
  } catch {
    throw new ShareLinkError('unavailable')
  }
  if (target.protocol !== 'https:' && target.protocol !== 'http:') {
    throw new ShareLinkError('unavailable')
  }
  target.username = ''
  target.password = ''
  target.search = ''
  target.hash = ''
  return target
}

/**
 * This deployment's own address: the build's base resolved against the page it
 * is on, and nothing past it but the mount's own document.
 *
 * The document stays when the page names it — `/pathways/index.html`, a relative
 * build's `…/index.html` in a bucket — because a host with no directory index (an
 * S3 or GCS endpoint, an Azure container) serves the app only there, and a link to
 * the bare directory would be an error page. A base written without its trailing
 * slash still names a directory. A base on another origin — an asset CDN, which
 * Vite allows — is not where the app is served, and neither is a missing one;
 * both leave the page's own path, which on an app with no router is its mount.
 */
function mountOf(current: URL, appBase?: string): URL {
  const mount = new URL(current.href)
  if (!appBase) return mount
  let based: URL
  try {
    based = new URL(appBase, current)
  } catch {
    return mount
  }
  if (based.origin !== current.origin) return mount
  const root = based.pathname.endsWith('/') ? based.pathname : `${based.pathname}/`
  const rest = current.pathname.startsWith(root) ? current.pathname.slice(root.length) : ''
  mount.pathname = /^[^/]+\.html?$/i.test(rest) ? current.pathname : root
  return mount
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = ''
  // Spreading a large typed array exceeds the argument limit in both Safari and
  // Chromium. Chunks keep the conversion bounded without a Node-only Buffer.
  const step = 0x8000
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step))
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function base64UrlToBytes(value: string): Uint8Array | null {
  if (!value || !/^[A-Za-z0-9_-]+$/.test(value)) return null
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  try {
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
  } catch {
    return null
  }
}

async function collect(
  stream: ReadableStream<Uint8Array>,
  limit = Number.POSITIVE_INFINITY,
): Promise<Uint8Array | null> {
  const reader = stream.getReader()
  const chunks: Uint8Array[] = []
  let size = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      size += value.byteLength
      if (size > limit) {
        await reader.cancel()
        return null
      }
      chunks.push(value)
    }
  } catch {
    return null
  }
  const out = new Uint8Array(size)
  let at = 0
  for (const chunk of chunks) {
    out.set(chunk, at)
    at += chunk.byteLength
  }
  return out
}

function ownedBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(buffer).set(bytes)
  return buffer
}

async function gzip(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null
  const stream = new Blob([ownedBuffer(bytes)]).stream().pipeThrough(new CompressionStream('gzip'))
  return collect(stream)
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof DecompressionStream === 'undefined') return null
  try {
    const stream = new Blob([ownedBuffer(bytes)])
      .stream()
      .pipeThrough(new DecompressionStream('gzip'))
    return await collect(stream, MAX_JSON_BYTES)
  } catch {
    return null
  }
}

/**
 * The document a link carries, built from the one the sender holds — which is
 * never touched. Exported so the thinning can be held by a test without a
 * browser's compression.
 *
 * THREE THINGS STAY HOME, and every one of them is still in the sender's file
 * exports, which carry the stored document whole.
 *
 * The retained raw reasoning traces. Up to 200 traces of 2,000 characters each
 * dwarfed everything else in the document, and they are the one part of the
 * history that is the sender's private thinking rather than the exploration's
 * record. The strip is the storage-pressure release's, exactly (see `release` in
 * persist.ts).
 *
 * The exact model-call receipts (`Op.modelCalls`). Each restates the graph as
 * its prompt and carries its output schema, so they grow with every pass: a
 * 130-claim exploration's receipts were over half its link. A receipt is exact
 * or it is nothing — a prompt blanked to save room would be a false record, and
 * an audit capsule built from the copy would certify it — so a receipt leaves
 * WHOLE, which is the absence historical ops have always had and every reader
 * already reports as "no exact receipt retained". The op ROW stays, with its
 * model stamp and its own spend. A narration keeps its words: they were in its
 * receipt's output, and the copy's thread shows them (`narration.shown`).
 *
 * The as-known replay's dated boards (`research.knowledgeHistory`), a third of
 * that same link. The copy's live graph IS the newest board, so what leaves is the
 * dated past, counted into `omittedBefore` — the replay's own "earlier states
 * outside this cache" line — rather than silently shortened.
 *
 * Nothing MORE may leave, and that is load-bearing rather than caution. Op rows
 * stay: `spend` is also the model roster (`modelsUsed`), `ops.length` is "how
 * many model passes", and an empty ledger is how an export decides it is looking
 * at the demo — a fork stripped of them exports "source: demo, 0 model passes"
 * over a model-stamped graph, and one later pass re-attributes the whole thing
 * to the recipient's model (invariant 3). A copy joins each executable test on a
 * conjecture through the op row of the pass that proposed it, because the
 * contract still names the sender's exploration (D290). Every node, check,
 * source record, reading, run account, Research Lab contract and execution
 * receipt stays, and goes back in through the one codec, which re-gates every
 * stored refutation (invariant 2). Provenance, not bulk.
 *
 * What left is counted on `session.shared.left`, added to whatever an earlier
 * link had already left out of this very document.
 */
export function linkPayload(doc: StoredSession): StoredSession {
  const s = doc.session
  let receipts = 0
  const ops = s.ops.map(({ thinking: _t, thinkingClipped: _c, modelCalls, ...rest }): Op => {
    if (!modelCalls?.length) return rest
    receipts++
    if (!rest.narration || rest.narration.shown !== undefined) return rest
    const shown = narrationWords({ ...rest, modelCalls })
    return shown ? { ...rest, narration: { ...rest.narration, shown } } : rest
  })
  const history = s.research?.knowledgeHistory
  const replayStates = history?.revisions.length ?? 0
  const research =
    s.research && history && replayStates
      ? {
          ...s.research,
          knowledgeHistory: { revisions: [], omittedBefore: history.omittedBefore + replayStates },
        }
      : s.research
  const before = s.shared?.left
  const left = {
    receipts: (before?.receipts ?? 0) + receipts,
    replayStates: (before?.replayStates ?? 0) + replayStates,
  }
  // Only what the link left. The sender's own read-first mark, when they are
  // passing on a copy they never continued, is theirs: the recipient's copy is
  // marked when it is opened (`linkCopy`), never by what the link says.
  const shared: SharedCopy | undefined = left.receipts || left.replayStates ? { left } : undefined
  const { shared: _mine, ...rest } = s
  return {
    ...doc,
    session: { ...rest, ops, ...(research ? { research } : {}), ...(shared ? { shared } : {}) },
  }
}

/**
 * Build a self-contained link to one validated-storage-shaped exploration: the
 * receiving app's address and a `#share=` fragment, nothing else (`shareTarget`).
 * `appBase` is the build's base path, Vite's `BASE_URL`.
 */
export async function explorationShareUrl(
  doc: StoredSession,
  fromHref: string,
  configuredHref?: string,
  appBase?: string,
): Promise<string> {
  const json = new TextEncoder().encode(storedSessionJson(linkPayload(doc)))
  if (json.byteLength > MAX_JSON_BYTES) throw new ShareLinkError('too-large')

  const compressed = await gzip(json)
  // The uncompressed format is a compatibility path for a browser old enough to
  // lack CompressionStream. It is accepted only when it still makes a navigable
  // link; no silent truncation, and no claim that a partial graph was shared.
  const format = compressed ? GZIP_FORMAT : JSON_FORMAT
  const payload = compressed ?? json
  const token = `${format}.${bytesToBase64Url(payload)}`
  if (token.length > MAX_TOKEN_CHARS) throw new ShareLinkError('too-large')

  const url = shareTarget(fromHref, configuredHref, appBase)
  url.hash = new URLSearchParams({ [PARAM]: token }).toString()
  return url.toString()
}

/**
 * Whether an address's fragment carries an exploration link. Synchronous and
 * without decoding: the intake takes the payload out of the address in its first
 * turn, before anything awaits, and a router deciding what an address is asks
 * this before it reads any routing query (D332). An address that carries a link
 * IS a link, whatever else it says.
 */
export function carriesExplorationLink(hash: string): boolean {
  return new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).has(PARAM)
}

/**
 * The address a link leaves once the intake has taken it in, whatever its read:
 * the app's mount (`mountOf`, from the build's base), without the fragment and
 * without the query (D332). The fragment goes so a reload opens the local copy
 * instead of importing another, and so a copied address never re-shares somebody
 * else's graph. The routing goes because a link made before D332 can still carry
 * that of the page it was copied from — in its query, or its path — and a reload
 * would follow it there: out of the copy and into the sender's shared graph.
 *
 * Absolute, never a bare path: a path beginning `//` is read as another host by
 * `history.replaceState`, which throws, and the link never opened.
 */
export function addressAfterLink(href: string, appBase?: string): string {
  const url = mountOf(new URL(href), appBase)
  url.search = ''
  url.hash = ''
  return url.href
}

/** Read, decompress and validate a share fragment. Never throws on link input. */
export async function readSharedExploration(hash: string): Promise<SharedExplorationRead> {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const token = params.get(PARAM)
  if (token === null) return { kind: 'none' }
  if (token.length > MAX_TOKEN_CHARS) return { kind: 'too-large' }

  const dot = token.indexOf('.')
  if (dot < 1) return { kind: 'invalid' }
  const format = token.slice(0, dot)
  const encoded = token.slice(dot + 1)
  const bytes = base64UrlToBytes(encoded)
  if (!bytes) return { kind: 'invalid' }

  let json: Uint8Array | null
  if (format === GZIP_FORMAT) {
    if (typeof DecompressionStream === 'undefined') return { kind: 'unsupported' }
    json = await gunzip(bytes)
  } else if (format === JSON_FORMAT) {
    json = bytes.byteLength <= MAX_JSON_BYTES ? bytes : null
  } else {
    return { kind: 'unsupported' }
  }
  if (!json) return { kind: bytes.byteLength > MAX_JSON_BYTES ? 'too-large' : 'invalid' }

  let text: string
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(json)
  } catch {
    return { kind: 'invalid' }
  }
  const doc = readStoredSessionJson(text)
  return doc ? { kind: 'ok', doc } : { kind: 'invalid' }
}

/**
 * A received graph is a fork, not the sender's document id installed locally.
 * Opening the same link twice therefore creates two independent explorations,
 * and sharing a link back to yourself cannot overwrite the graph it came from.
 *
 * Only the document's own id is new. Nothing inside is rebound to it: a Research
 * Lab contract keeps the exploration it was proposed in, which every receipt of
 * it has digested, and its conjecture finds it through the ledger (D290).
 */
export function sharedExplorationCopy(
  doc: StoredSession,
  id = uid('s'),
  savedAt = Date.now(),
): StoredSession {
  // A fork or an opened file is the reader's own from the first press, so the
  // read-first mark never rides a copy made here — only `linkCopy` sets it.
  // What an earlier link left out stays: this copy is missing it too.
  const { shared, ...session } = doc.session
  const left = shared?.left
  return { ...doc, savedAt, session: { ...session, id, ...(left ? { shared: { left } } : {}) } }
}

/**
 * The copy a link opens: a new local document, marked read-first. The one door
 * that sets the mark, so a copy is read-first exactly when somebody opened it
 * from a link — never because the link's own bytes said so.
 */
export function linkCopy(doc: StoredSession, id = uid('s'), savedAt = Date.now()): StoredSession {
  const copy = sharedExplorationCopy(doc, id, savedAt)
  return {
    ...copy,
    session: { ...copy.session, shared: { ...copy.session.shared, readFirst: true } },
  }
}

/**
 * The copy, continued: the reader pressed Continue working, and it is now their
 * exploration. The mark goes for good; what the link left out stays counted,
 * because the document is still missing it. Returns the same session when there
 * was no mark, so a caller can compare by reference.
 */
export function continuedCopy<T extends { shared?: SharedCopy }>(session: T): T {
  if (!session.shared?.readFirst) return session
  const { shared, ...rest } = session
  return (shared.left ? { ...rest, shared: { left: shared.left } } : rest) as T
}
